import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { API_BASE } from "../../lib/site";

const PRODUCTS = [
    { value: "standing", label: "NFC Standing Card — $45", hasColor: true },
    { value: "plaque", label: "NFC Plaque — $65", hasColor: true },
    { value: "sticker", label: "NFC Sticker — $10", hasColor: false },
];

export default function QuoteForm({ compact = false }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        business_name: "",
        product: "plaque",
        color: "Black",
        quantity: 1,
        notes: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));
    const currentProduct = PRODUCTS.find((p) => p.value === form.product);
    const showColor = !!currentProduct?.hasColor;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please add your name and email.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_BASE}/orders`, {
                ...form,
                quantity: Number(form.quantity) || 1,
                color: showColor ? form.color : "N/A",
            });

            // TikTok Pixel — fire Lead conversion event
            const productPrices = { standing: 45, plaque: 65, sticker: 10 };
            const qty = Number(form.quantity) || 1;
            const value = (productPrices[form.product] || 0) * qty;
            try {
                if (typeof window !== "undefined" && window.ttq) {
                    window.ttq.track("Lead", {
                        contents: [
                            {
                                content_id: form.product,
                                content_type: "product",
                                content_name: `NFC ${form.product}`,
                                quantity: qty,
                                price: productPrices[form.product] || 0,
                            },
                        ],
                        value: value,
                        currency: "USD",
                    });
                }
            } catch (pixelErr) {
                console.warn("TikTok pixel error:", pixelErr);
            }

            toast.success(
                `Quote sent! We'll reach out to you at ${form.email} shortly.`,
            );
            setForm({
                name: "",
                email: "",
                phone: "",
                business_name: "",
                product: "plaque",
                color: "Black",
                quantity: 1,
                notes: "",
            });
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "bg-black/40 border-white/10 text-white placeholder:text-zinc-600 h-11 rounded-xl focus-visible:ring-blue-500/40";

    return (
        <form
            onSubmit={submit}
            data-testid="quote-form"
            className={`glass rounded-3xl ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"} space-y-3.5`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="label-eyebrow !text-blue-300">Get a quote</div>
                    <div className="font-display text-xl font-light text-white mt-1">
                        Tell us what you need
                    </div>
                </div>
                <ArrowRight className="w-5 h-5 text-blue-400 hidden sm:block" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label htmlFor="qf-name" className="text-zinc-400 text-[10px] uppercase tracking-widest">Name</Label>
                    <Input id="qf-name" data-testid="form-input-name" value={form.name} onChange={onChange("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="qf-email" className="text-zinc-400 text-[10px] uppercase tracking-widest">Email</Label>
                    <Input id="qf-email" type="email" data-testid="form-input-email" value={form.email} onChange={onChange("email")} placeholder="you@business.com" className={inputCls} />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label htmlFor="qf-phone" className="text-zinc-400 text-[10px] uppercase tracking-widest">Phone</Label>
                    <Input id="qf-phone" data-testid="form-input-phone" value={form.phone} onChange={onChange("phone")} placeholder="(555) 555-5555" className={inputCls} />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="qf-biz" className="text-zinc-400 text-[10px] uppercase tracking-widest">Business</Label>
                    <Input id="qf-biz" data-testid="form-input-business" value={form.business_name} onChange={onChange("business_name")} placeholder="Your business" className={inputCls} />
                </div>
            </div>

            <div className={`grid gap-3 ${showColor ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[10px] uppercase tracking-widest">Product</Label>
                    <Select value={form.product} onValueChange={(v) => setForm((f) => ({ ...f, product: v }))}>
                        <SelectTrigger data-testid="form-select-product" className="bg-black/40 border-white/10 text-white h-11 rounded-xl text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f0f11] border-white/10 text-white">
                            {PRODUCTS.map((p) => (
                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {showColor && (
                    <div className="space-y-1.5">
                        <Label className="text-zinc-400 text-[10px] uppercase tracking-widest">Color</Label>
                        <Select value={form.color} onValueChange={(v) => setForm((f) => ({ ...f, color: v }))}>
                            <SelectTrigger data-testid="form-select-color" className="bg-black/40 border-white/10 text-white h-11 rounded-xl text-sm">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0f0f11] border-white/10 text-white">
                                <SelectItem value="Black">Black</SelectItem>
                                <SelectItem value="White">White</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <div className="space-y-1.5">
                    <Label htmlFor="qf-qty" className="text-zinc-400 text-[10px] uppercase tracking-widest">Quantity</Label>
                    <Input id="qf-qty" type="number" min={1} data-testid="form-input-quantity" value={form.quantity} onChange={onChange("quantity")} className={inputCls} />
                </div>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="qf-notes" className="text-zinc-400 text-[10px] uppercase tracking-widest">Notes</Label>
                <Textarea id="qf-notes" data-testid="form-input-notes" value={form.notes} onChange={onChange("notes")} placeholder="Anything else we should know..." rows={2} className="bg-black/40 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus-visible:ring-blue-500/40 resize-none text-sm" />
            </div>

            <Button type="submit" data-testid="form-submit" disabled={loading} className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm shadow-[0_0_30px_rgba(37,99,235,0.4)] hover:shadow-[0_0_50px_rgba(37,99,235,0.6)] transition-all">
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>) : (<>Send Quote Request <Send className="w-4 h-4 ml-2" /></>)}
            </Button>
        </form>
    );
}
